import {useQuery} from '@apollo/client';

import type {GetScientificModels} from './__generated/GetScientificModels';
import {
  getAvailableGenerativeModels,
  getAvailableStructurePredictionModels,
} from './ScientificModelConstants';
import {GET_SCIENTIFIC_MODELS_QUERY} from './ScientificModelsQueries';

/**
 * Returns the set of installed scientific model system names for the current tenant,
 * sourced from the backend (AIModelTable + app-catalog).
 * While loading, returns undefined so callers can distinguish "not loaded" from "empty".
 */
function useInstalledScientificModels(): Set<string> | undefined {
  const {data, loading} = useQuery<GetScientificModels>(GET_SCIENTIFIC_MODELS_QUERY);

  if (loading || !data) {
    return undefined;
  }

  return new Set(data.scientificModels.map((m) => m.systemName));
}

/**
 * Returns the structure prediction models available on the current tenant, already filtered
 * by installation state. While the underlying query is loading, returns undefined so callers
 * can render a loading state instead of an empty list.
 */
export function useAvailableStructurePredictionModels(): Array<string> | undefined {
  const installedModels = useInstalledScientificModels();
  return installedModels == null ? undefined : getAvailableStructurePredictionModels(installedModels);
}

/**
 * Returns the generative models available on the current tenant, already filtered by
 * installation state. While the underlying query is loading, returns undefined so callers
 * can render a loading state instead of an empty list.
 */
export function useAvailableGenerativeModels(): Array<string> | undefined {
  const installedModels = useInstalledScientificModels();
  return installedModels == null ? undefined : getAvailableGenerativeModels(installedModels);
}
